(() => {
  if (window.KJCart) return;

  const ROOT = '[data-kj-cart-root]';
  const PART = 'data-kj-cart-part';
  const routes = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  function sectionIds() {
    return [...document.querySelectorAll('[data-kj-cart-section]')].map((el) => el.dataset.kjCartSection);
  }

  function parse(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  async function postJSON(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || 'Something went wrong. Please try again.');
    }
    return data;
  }

  const KJCart = {
    busy: 0,

    withSections(body) {
      return { ...body, sections: sectionIds(), sections_url: window.location.pathname };
    },

    async change(key, quantity) {
      const state = await postJSON(`${routes}cart/change.js`, this.withSections({ id: key, quantity }));
      this.render(state.sections);
      return state;
    },

    async add(items) {
      const state = await postJSON(`${routes}cart/add.js`, this.withSections({ items }));
      this.render(state.sections);
      return state;
    },

    async refresh() {
      const ids = sectionIds();
      if (!ids.length) return;
      const response = await fetch(`${window.location.pathname}?sections=${ids.join(',')}`);
      if (!response.ok) return;
      this.render(await response.json());
    },

    updateNote(note) {
      clearTimeout(this.noteTimer);
      this.noteTimer = setTimeout(() => {
        postJSON(`${routes}cart/update.js`, { note }).catch(() => {});
      }, 400);
    },

    render(sections) {
      if (!sections) return;
      let count = null;

      Object.entries(sections).forEach(([id, html]) => {
        if (!html) return;
        const live = document.getElementById(`shopify-section-${id}`);
        if (!live) return;
        const next = parse(html);
        const nextRoot = next.querySelector(ROOT);
        const liveRoot = live.querySelector(ROOT);
        if (!nextRoot || !liveRoot) return;

        liveRoot.classList.toggle('is-empty', nextRoot.classList.contains('is-empty'));
        liveRoot.dataset.count = nextRoot.dataset.count;
        count = Number(nextRoot.dataset.count);

        liveRoot.querySelectorAll(`[${PART}]`).forEach((part) => {
          const name = part.getAttribute(PART);
          const fresh = nextRoot.querySelector(`[${PART}="${name}"]`);
          if (!fresh) return;
          if (part.hasAttribute('data-kj-cart-keep')) {
            ['productId', 'cartIds'].forEach((key) => {
              part.dataset[key] = fresh.dataset[key] || '';
            });
            if (typeof part.update === 'function') part.update();
            return;
          }
          part.innerHTML = fresh.innerHTML;
        });
      });

      if (count !== null) this.updateCount(count);
      document.dispatchEvent(new CustomEvent('kj:cart:updated', { detail: { count } }));
    },

    updateCount(count) {
      document.querySelectorAll('[data-bind="itemCount"]').forEach((el) => {
        el.textContent = count;
      });
      document.querySelectorAll('[data-ajax-cart-trigger]').forEach((el) => {
        el.classList.toggle('has-cart-count', count > 0);
      });
    },

    drawer() {
      return document.querySelector('kj-cart-drawer');
    },

    open() {
      const drawer = this.drawer();
      if (drawer) drawer.open();
    },

    close() {
      const drawer = this.drawer();
      if (drawer) drawer.close();
    },
  };

  window.KJCart = KJCart;

  class KJCartDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.kj-drawer__panel');
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.close();
        if (event.key === 'Tab') this.trapFocus(event);
      });
    }

    open() {
      if (this.classList.contains('is-open')) return;
      this.lastFocus = document.activeElement;
      this.setAttribute('aria-hidden', 'false');
      this.classList.add('is-open');
      document.documentElement.classList.add('kj-cart-locked');
      requestAnimationFrame(() => this.panel.focus({ preventScroll: true }));
      const upsell = this.querySelector('kj-cart-upsell');
      if (upsell) upsell.activate();
    }

    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('kj-cart-locked');
      if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus({ preventScroll: true });
    }

    trapFocus(event) {
      const focusable = [
        ...this.panel.querySelectorAll('a[href], button:not([disabled]), input, select, textarea, summary'),
      ].filter((el) => el.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  class KJCartUpsell extends HTMLElement {
    connectedCallback() {
      this.results = this.querySelector('[data-kj-upsell-slot]');
      this.loadedFor = null;
      if (!this.hasAttribute('data-lazy')) this.activate();
    }

    activate() {
      this.active = true;
      this.update();
    }

    async update() {
      if (!this.active) return;
      const productId = this.dataset.productId || '';
      if (this.loadedFor === productId) {
        this.prune();
        return;
      }
      this.loadedFor = productId;

      let results = null;
      if (productId) {
        results = await this.fetchResults('complementary', productId);
        if (!results || !results.querySelector('.kj-upsell__item')) {
          results = await this.fetchResults('related', productId);
        }
      } else {
        results = await this.fetchFallback();
      }
      if (this.loadedFor !== productId) return;

      this.results.innerHTML = results ? results.innerHTML : '';
      this.prune();
    }

    async fetchResults(intent, productId) {
      const limit = Math.min(10, Number(this.dataset.limit || 4) + 4);
      const url = `${routes}recommendations/products?section_id=kj-cart-upsell&product_id=${productId}&limit=${limit}&intent=${intent}`;
      try {
        const response = await fetch(url);
        if (!response.ok) return null;
        return parse(await response.text()).querySelector('[data-kj-upsell-results]');
      } catch (error) {
        return null;
      }
    }

    async fetchFallback() {
      try {
        const response = await fetch(`${window.location.pathname}?section_id=kj-cart-upsell`);
        if (!response.ok) return null;
        return parse(await response.text()).querySelector('[data-kj-upsell-results]');
      } catch (error) {
        return null;
      }
    }

    prune() {
      const inCart = (this.dataset.cartIds || '').split(',').filter(Boolean);
      const items = [...this.querySelectorAll('.kj-upsell__item')];
      const limit = Number(this.dataset.limit || 4);
      let visible = 0;
      items.forEach((item) => {
        const hide = inCart.includes(item.dataset.productId) || visible >= limit;
        item.hidden = hide;
        if (!hide) visible += 1;
      });
      this.hidden = visible === 0;
    }
  }

  customElements.define('kj-cart-drawer', KJCartDrawer);
  customElements.define('kj-cart-upsell', KJCartUpsell);

  function lineFor(el) {
    return el.closest('[data-kj-line]');
  }

  function showLineError(line, message) {
    const error = line && line.querySelector('[data-kj-line-error]');
    if (!error) return;
    error.textContent = message;
    error.hidden = false;
  }

  async function changeLine(line, quantity) {
    if (!line || line.classList.contains('is-busy')) return;
    line.classList.add('is-busy');
    try {
      await KJCart.change(line.dataset.kjLine, Math.max(0, quantity));
    } catch (error) {
      line.classList.remove('is-busy');
      showLineError(line, error.message);
      const input = line.querySelector('[data-kj-qty-input]');
      if (input) input.value = line.dataset.kjQty;
    }
  }

  document.addEventListener('click', (event) => {
    const close = event.target.closest('[data-kj-cart-close]');
    if (close) {
      event.preventDefault();
      KJCart.close();
      return;
    }

    const step = event.target.closest('[data-kj-qty-step]');
    if (step) {
      const line = lineFor(step);
      changeLine(line, Number(line.dataset.kjQty) + Number(step.dataset.kjQtyStep));
      return;
    }

    const remove = event.target.closest('[data-kj-remove]');
    if (remove) changeLine(lineFor(remove), 0);
  });

  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-kj-qty-input]');
    if (!input) return;
    const line = lineFor(input);
    const quantity = parseInt(input.value, 10);
    if (Number.isNaN(quantity) || String(quantity) === line.dataset.kjQty) return;
    changeLine(line, quantity);
  });

  document.addEventListener('input', (event) => {
    const note = event.target.closest('[data-kj-note]');
    if (!note) return;
    document.querySelectorAll('[data-kj-note]').forEach((el) => {
      if (el !== note) el.value = note.value;
    });
    KJCart.updateNote(note.value);
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-kj-upsell-form]');
    if (!form) return;
    event.preventDefault();
    const button = form.querySelector('button[type="submit"]');
    const counts = new Map();
    new FormData(form).getAll('id').forEach((value) => {
      const id = Number(value);
      if (!id) return;
      counts.set(id, (counts.get(id) || 0) + 1);
    });
    const items = [...counts.entries()].map(([id, quantity]) => ({ id, quantity }));
    if (!items.length || button.disabled) return;
    button.disabled = true;
    button.classList.add('is-busy');
    try {
      await KJCart.add(items);
    } catch (error) {
      button.disabled = false;
      button.classList.remove('is-busy');
      button.title = error.message;
    }
  });

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) KJCart.refresh();
  });

  function patchFlex() {
    const flex = window.PXUTheme && window.PXUTheme.jsAjaxCart;
    if (!flex || flex.kjPatched || !KJCart.drawer()) return;
    flex.kjPatched = true;
    flex.initializeAjaxCart = () => {};
    flex.initializeAjaxCartOnMobile = () => {};
    flex.showDrawer = () => KJCart.open();
    flex.hideDrawer = () => KJCart.close();
    flex.hideMiniCart = () => {};
    flex.updateView = () => KJCart.refresh();
    flex.removeFromCart = () => KJCart.refresh();
  }

  patchFlex();
  document.addEventListener('DOMContentLoaded', patchFlex);
  window.addEventListener('load', patchFlex);
})();
